import { isValidElement, useCallback, useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { useTheme } from '@mui/material/styles';

import { MRT_Localization_FR as MRTLocalizationFR } from 'material-react-table/locales/fr';
import { MRT_Localization_EN as MRTLocalizationEN } from 'material-react-table/locales/en';

import linkifyHtml from 'linkify-html';

import { Modal } from '@/ui/modal/modal';
import { MRTTable as Table, type MRT_ColumnDef as MRTColumnDef } from '@/ui/table/table';
import { Box } from '@/ui/layout';
import { CircularProgress } from '@/ui/circular-progress/circular-progress';
import { useUIController } from '@/core/controllers/use-controllers';
import type { TypeFieldEntry } from '@/api/types/map-schema-types';
import { UseHtmlToReact } from '@/core/components/common/hooks/use-html-to-react';
import { useStoreUIActiveFocusItem } from '@/core/stores/states/ui-state';
import { useStoreAppDisplayLanguage, useStoreAppShellContainer } from '@/core/stores/states/app-state';
import { useStoreDataTableAllFeaturesDataArray } from '@/core/stores/states/data-table-state';
import { useStoreLayerNameSet, useStoreLayerSelectedLayerPath } from '@/core/stores/states/layer-state';
import { logger } from '@/core/utils/logger';
import { sanitizeHtmlContent, enhanceLinksAccessibility } from '@/core/utils/utilities';
import type { SxStyles } from '@/ui/style/types';
import { dataTableModalContentStyle, getDataTableModalContentTextStyle, getSxClasses } from './data-table-style';
import { useFeatureFieldInfos } from './hooks/useFeatureFieldInfos';

/** Linkify configuration options for URL detection and formatting. */
const linkifyOptions = {
  attributes: {
    target: '_blank',
    rel: 'noopener noreferrer',
  },
  defaultProtocol: 'https',
  format: {
    url: (value: string) => (value.length > 50 ? `${value.slice(0, 40)}…${value.slice(value.length - 10)}` : value),
  },
  ignoreTags: ['script', 'style', 'img'],
};

/**
 * Renders a lightweight read-only data table in a modal window.
 *
 * @returns The data table modal element
 */
export default function DataTableModal(): JSX.Element {
  // Log
  logger.logTraceRender('components/data-table/data-table-modal');

  const { t } = useTranslation();

  const theme = useTheme();
  const memoSxClasses = useMemo((): SxStyles => getSxClasses(theme), [theme]);
  const memoDataTableModalContentTextStyle = useMemo(() => getDataTableModalContentTextStyle(theme), [theme]);

  const [isLoading, setIsLoading] = useState(true);

  // get store function
  const activeModalId = useStoreUIActiveFocusItem().activeElementId;
  const selectedLayerPath = useStoreLayerSelectedLayerPath();
  const layersData = useStoreDataTableAllFeaturesDataArray();
  const language = useStoreAppDisplayLanguage();
  const shellContainer = useStoreAppShellContainer();
  const layerNames = useStoreLayerNameSet();
  const uiController = useUIController();

  const dataTableLocalization = language === 'fr' ? MRTLocalizationFR : MRTLocalizationEN;

  // Create columns for data table.
  const mappedLayerData = useFeatureFieldInfos(layersData);

  /**
   * Finds the layer data matching the selected layer path.
   */
  const memoLayer = useMemo(() => {
    // Log
    logger.logTraceUseMemo('DATA-TABLE-MODAL - layer', mappedLayerData, selectedLayerPath);

    return mappedLayerData?.find((layerData) => layerData.layerPath === selectedLayerPath);
  }, [mappedLayerData, selectedLayerPath]);

  /**
   * Creates a data table body cell.
   *
   * @param cellValue - Cell value to be displayed
   * @returns The cell element
   */
  const getCellValue = useCallback(
    (cellValue: string | number | JSX.Element): JSX.Element => {
      return typeof cellValue === 'string' || typeof cellValue === 'number' ? (
        <Box component="div" sx={memoSxClasses.tableCell}>
          <UseHtmlToReact
            htmlContent={sanitizeHtmlContent(
              enhanceLinksAccessibility(linkifyHtml(cellValue.toString(), linkifyOptions), t('general.opensInNewTab'))
            )}
            itemOptions={{ tabIndex: 0 }}
          />
        </Box>
      ) : (
        <Box component="div" sx={memoSxClasses.tableCell}>
          {cellValue}
        </Box>
      );
    },
    [memoSxClasses.tableCell, t]
  );

  /**
   * Creates a table header cell.
   *
   * @param header - Value to be displayed in the header
   * @returns The header element
   */
  const getTableHeader = useCallback((header: string): JSX.Element => {
    return (
      <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
        {header}
      </Box>
    );
  }, []);

  /**
   * Builds the column definitions from the layer field infos.
   */
  const memoColumns = useMemo<MRTColumnDef<Partial<Record<string, TypeFieldEntry>>>[]>(() => {
    // Log
    logger.logTraceUseMemo('DATA-TABLE-MODAL - columns', memoLayer?.fieldInfos);

    if (!memoLayer?.fieldInfos) {
      return [];
    }
    const entries = Object.entries(memoLayer?.fieldInfos ?? {});
    const columnList = [] as MRTColumnDef<Partial<Record<string, TypeFieldEntry>>>[];

    entries.forEach(([key, value]) => {
      // Skip the internal geoviewID field and any malformed column with a blank key (MRT rejects a falsy id with accessorFn)
      if (value?.alias !== 'geoviewID' && key.trim())
        columnList.push({
          id: key,
          accessorFn: (row) => {
            // check if row is valid react element.
            if (isValidElement(row[key])) {
              return row[key];
            }
            if (typeof row[key]?.value === 'string' || typeof row[key]?.value === 'number') {
              return row[key]?.value ?? '';
            }
            return '';
          },
          header: value?.alias ?? '',
          Cell: ({ cell }) => getCellValue(cell.getValue() as string | number | JSX.Element),
          Header: ({ column }) => getTableHeader(column.columnDef.header),
          maxSize: 120,
        });
    });

    return columnList;
  }, [memoLayer?.fieldInfos, getCellValue, getTableHeader]);

  /**
   * Extracts the row data from the layer features.
   */
  const memoRows = useMemo(() => {
    // Log
    logger.logTraceUseMemo('DATA-TABLE-MODAL - rows', memoLayer?.features);

    return (
      memoLayer?.features?.map((feature) => {
        return feature.fieldInfo;
      }) ?? []
    );
  }, [memoLayer?.features]);

  /**
   * Updates loading state based on query status of the selected layer.
   */
  useEffect(() => {
    // Log
    logger.logTraceUseEffect('DATA-TABLE-MODAL - query status');

    // Get feature info result for selected layer to check if it is loading
    const selectedLayerData = layersData.find((_layer) => _layer.layerPath === selectedLayerPath);

    if (selectedLayerData?.queryStatus === 'processing') {
      setIsLoading(true);
    } else setIsLoading(false);
  }, [layersData, selectedLayerPath]);

  return (
    <Modal
      modalId="layerDataTable"
      open={activeModalId === 'layerDataTable'}
      onClose={() => uiController.disableFocusTrap()}
      title={`${t('legend.tableDetails')} ${layerNames[selectedLayerPath!] || ''}`}
      container={shellContainer}
      width="90vw"
      contentModal={
        <>
          {isLoading && (
            <Box sx={memoSxClasses.loadingSpinnerContainer}>
              <CircularProgress
                isLoaded={!isLoading}
                style={{
                  backgroundColor: 'inherit',
                }}
              />
            </Box>
          )}
          {!isLoading && (
            <Table
              columns={memoColumns}
              data={memoRows}
              enableColumnActions={false}
              enablePagination={(memoLayer?.features?.length ?? 0) > 50}
              enableBottomToolbar={(memoLayer?.features?.length ?? 0) > 50}
              initialState={{ density: 'compact', pagination: { pageSize: 50, pageIndex: 0 } }}
              muiPaginationProps={{
                rowsPerPageOptions: [50, 100],
              }}
              muiTableContainerProps={{ sx: memoSxClasses.dataTableModalTableContainer }}
              enableStickyHeader
              enableSorting
              positionToolbarAlertBanner="none"
              localization={dataTableLocalization}
              enableGlobalFilter={false}
              enableColumnFilters={false}
              enableDensityToggle={false}
              enableFilters={false}
              enableFullScreenToggle={false}
              enableHiding={false}
              enableTopToolbar={false}
            />
          )}
        </>
      }
      contentStyle={dataTableModalContentStyle}
      contentTextStyle={memoDataTableModalContentTextStyle}
    />
  );
}
